import {
    clamp01,
    easeInOut,
    getMaxPixelSize,
    drawCoverImage,
} from './pixel-cover-draw.js';

const getActiveImageElement = (mediaWrap) => {
    if (!mediaWrap) {
        return null;
    }
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
        const mobileEl = mediaWrap.querySelector('.f-overlay-teaser__image--mobile, .f-featured-teaser__image--mobile');
        if (mobileEl) {
            return mobileEl;
        }
    } else {
        const desktopEl = mediaWrap.querySelector('.f-overlay-teaser__image--desktop, .f-featured-teaser__image--desktop');
        if (desktopEl) {
            return desktopEl;
        }
    }
    return mediaWrap.querySelector('img');
};

const setupStandaloneTeaser = (teaser) => {
    const isOverlay = teaser.classList.contains('f-overlay-teaser');
    const mediaWrap = teaser.querySelector(
        isOverlay ? '.f-overlay-teaser__media' : '.f-featured-teaser__media',
    );
    if (!mediaWrap) {
        return null;
    }

    const hasVideo = mediaWrap.querySelector('video') !== null;
    if (hasVideo) {
        return null;
    }

    const canvasClass = isOverlay
        ? 'f-overlay-teaser__pixel-canvas'
        : 'f-featured-teaser__pixel-canvas';

    let canvas = mediaWrap.querySelector(`.${canvasClass}`);
    if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.className = canvasClass;
        mediaWrap.appendChild(canvas);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }

    const offscreenCanvas = document.createElement('canvas');
    const offscreenCtx = offscreenCanvas.getContext('2d');

    let canvasWidth = 0;
    let canvasHeight = 0;
    let isIntersecting = false;
    let lastPixelDrawn = -1;

    const resizeCanvas = () => {
        const rect = mediaWrap.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = Math.round(rect.width * dpr);
        const h = Math.round(rect.height * dpr);

        if (canvasWidth !== w || canvasHeight !== h) {
            canvasWidth = w;
            canvasHeight = h;
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            lastPixelDrawn = -1;
        }
    };

    const update = () => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) {
            teaser.classList.remove('is-pixelating');
            return;
        }

        const vh = window.innerHeight;
        const rect = teaser.getBoundingClientRect();

        // If teaser is offscreen, avoid work
        if (rect.bottom <= 0 || rect.top >= vh) {
            return;
        }

        // Progress 0 when teaser enters bottom of viewport (top == vh)
        // Progress 1 when almost fully in viewport (top <= vh * 0.25, i.e., traveled 75% of viewport height)
        const progress = clamp01((vh - rect.top) / (vh * 0.75));

        if (progress >= 1) {
            if (teaser.classList.contains('is-pixelating')) {
                teaser.classList.remove('is-pixelating');
            }
            lastPixelDrawn = 1;
            return;
        }

        resizeCanvas();
        if (!canvasWidth || !canvasHeight) {
            return;
        }

        const img = getActiveImageElement(mediaWrap);
        if (!img || !img.complete || !img.naturalWidth) {
            return;
        }

        const maxPixel = getMaxPixelSize();
        const pixelSize = Math.max(1, Math.round(1 + (maxPixel - 1) * (1 - easeInOut(progress))));

        if (pixelSize <= 1) {
            teaser.classList.remove('is-pixelating');
            lastPixelDrawn = 1;
            return;
        }

        if (pixelSize !== lastPixelDrawn) {
            lastPixelDrawn = pixelSize;
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            drawCoverImage({
                ctx,
                offscreenCtx,
                offscreenCanvas,
                img,
                canvasWidth,
                canvasHeight,
                pixelSize,
                opacity: 1,
            });
        }

        teaser.classList.add('is-pixelating');
    };

    const img = getActiveImageElement(mediaWrap);
    if (img && !img.complete) {
        img.addEventListener('load', () => {
            lastPixelDrawn = -1;
            if (isIntersecting) {
                update();
            }
        });
    }

    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    isIntersecting = entry.isIntersecting;
                    if (isIntersecting) {
                        update();
                    }
                });
            },
            { rootMargin: '100px 0px 100px 0px' },
        );
        observer.observe(teaser);
    } else {
        isIntersecting = true;
    }

    return () => {
        if (isIntersecting) {
            update();
        }
    };
};

export const initStandaloneTeaserPixelate = () => {
    // Collect all teasers that are NOT part of a multi-teaser container
    const allTeasers = Array.from(document.querySelectorAll('.f-featured-teaser, .f-overlay-teaser'));
    const standaloneTeasers = allTeasers.filter((teaser) => {
        const container = teaser.closest('.f-teaser-container');
        if (!container) {
            return true;
        }
        // If inside a container with only 1 teaser, container logic aborts (length <= 1), so handle as standalone
        const teasersInContainer = container.querySelectorAll('.f-featured-teaser, .f-overlay-teaser');
        return teasersInContainer.length <= 1;
    });

    const updaters = standaloneTeasers
        .map((teaser) => setupStandaloneTeaser(teaser))
        .filter(Boolean);

    return () => {
        updaters.forEach((update) => update());
    };
};
